// AI Service for FusionPM
// This service handles AI-powered features including chat, suggestions, and automation

const AI_ENDPOINTS = {
  openai: 'https://api.openai.com/v1/chat/completions',
  local: '/api/ai/chat',
};

// System prompts for different AI contexts
const SYSTEM_PROMPTS = {
  general: `You are an AI assistant for FusionPM, a project management system for sign manufacturing. 
You help users with:
- Creating and managing jobs, estimates, and permits
- Tracking production workflows
- Managing inventory and materials
- Scheduling installations
- Generating reports and analytics
- CRM tasks (clients, vendors, contacts)

Be concise, helpful, and focus on actionable responses. When users ask about creating something, 
provide step-by-step guidance or offer to help them get started.`,

  estimating: `You are an AI estimating assistant for sign manufacturing projects.
Help users calculate material costs, labor hours, and pricing. Consider:
- Sign types (channel letters, monuments, pylons, etc.)
- Materials (aluminum, acrylic, LED modules, etc.)
- Labor rates and complexity factors
- Permit requirements and timeline`,

  production: `You are a production planning assistant.
Help with scheduling, workflow optimization, and resource allocation.
Consider machine capacity, material availability, and deadline management.`,

  inventory: `You are an inventory management assistant.
Help track stock levels, suggest reorder points, and optimize material usage.`,
};

// Mock AI responses for demo (when no API key is configured)
const MOCK_RESPONSES = {
  default: [
    "I can help you with that! Would you like me to walk you through the steps?",
    "Let me look into that for you. Here's what I found...",
    "Great question! Here's how you can do that in FusionPM...",
    "I understand. Let me provide some suggestions...",
  ],
  job: [
    "To create a new job, go to Jobs → Add New Job. You'll need the client name, job type, and estimated completion date.",
    "I can help you track job progress. Would you like to see the production status or update milestones?",
    "For job scheduling, I recommend using the Production Calendar. It shows all active jobs and their timelines.",
  ],
  estimate: [
    "For estimates, you can start from the Estimates page. I can help calculate material costs if you provide the sign specifications.",
    "Channel letter estimates typically include: face material, returns, LEDs, power supplies, and labor. What size are you quoting?",
    "Would you like me to generate an estimate template based on your job type?",
  ],
  permit: [
    "Permit requirements vary by municipality. What city is the installation in?",
    "I can help track your permit status. The typical workflow is: Submit → Review → Revisions → Approval → Pickup.",
    "Most sign permits require: site plan, sign drawings, property owner authorization, and fee payment.",
  ],
  inventory: [
    "I see you're asking about inventory. Would you like to check stock levels or create a purchase order?",
    "Low stock alert: You may need to reorder aluminum sheets and LED modules soon.",
    "For inventory adjustments, use the Adjustment page to record usage or returns.",
  ],
  production: [
    "The production schedule shows 5 jobs due this week. Would you like to see the priority list?",
    "For production efficiency, I recommend batching similar job types together.",
    "Current production capacity is at 75%. You have room for 2-3 additional small jobs.",
  ],
};

// Analyze user intent from message
function analyzeIntent(message) {
  const lower = message.toLowerCase();
  
  if (lower.includes('job') || lower.includes('project')) return 'job';
  if (lower.includes('estimate') || lower.includes('quote') || lower.includes('price')) return 'estimate';
  if (lower.includes('permit') || lower.includes('city') || lower.includes('approval')) return 'permit';
  if (lower.includes('inventory') || lower.includes('stock') || lower.includes('material')) return 'inventory';
  if (lower.includes('production') || lower.includes('schedule') || lower.includes('fabrication')) return 'production';
  if (lower.includes('client') || lower.includes('customer') || lower.includes('contact')) return 'crm';
  if (lower.includes('report') || lower.includes('analytics') || lower.includes('dashboard')) return 'report';
  
  return 'default';
}

// Get a mock response based on intent
function getMockResponse(message) {
  const intent = analyzeIntent(message);
  const responses = MOCK_RESPONSES[intent] || MOCK_RESPONSES.default;
  return responses[Math.floor(Math.random() * responses.length)];
}

// Extract action suggestions from message
function extractActions(message, intent) {
  const actions = [];
  const lower = message.toLowerCase();
  
  if (intent === 'job' && (lower.includes('create') || lower.includes('new'))) {
    actions.push({ type: 'navigate', label: 'Create New Job', target: 'jobs' });
  }
  if (intent === 'estimate' && (lower.includes('create') || lower.includes('new'))) {
    actions.push({ type: 'navigate', label: 'Create Estimate', target: 'estimates' });
  }
  if (intent === 'permit') {
    actions.push({ type: 'navigate', label: 'View Permits', target: 'permits' });
  }
  if (intent === 'inventory') {
    actions.push({ type: 'navigate', label: 'Check Inventory', target: 'inventory' });
  }
  if (intent === 'production') {
    actions.push({ type: 'navigate', label: 'Production Schedule', target: 'production' });
  }
  if (lower.includes('report')) {
    actions.push({ type: 'navigate', label: 'View Reports', target: 'reports' });
  }
  
  return actions;
}

// Main chat function
export async function sendChatMessage(message, context = 'general', history = []) {
  const intent = analyzeIntent(message);
  
  // For demo purposes, use mock responses
  // In production, this would call the actual AI API
  const useMockResponses = true; // Set to false when API is configured
  
  if (useMockResponses) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 1000));
    
    const response = getMockResponse(message);
    const actions = extractActions(message, intent);
    
    return {
      success: true,
      message: response,
      intent,
      actions,
      suggestions: getSuggestions(intent),
    };
  }
  
  // Real API call (when configured)
  try {
    const apiKey = localStorage.getItem('openai_api_key');
    if (!apiKey) {
      throw new Error('No API key configured');
    }
    
    const messages = [
      { role: 'system', content: SYSTEM_PROMPTS[context] || SYSTEM_PROMPTS.general },
      ...history.slice(-10), // Last 10 messages for context
      { role: 'user', content: message },
    ];
    
    const response = await fetch(AI_ENDPOINTS.openai, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages,
        temperature: 0.7,
        max_tokens: 500,
      }),
    });
    
    if (!response.ok) {
      throw new Error('API request failed');
    }
    
    const data = await response.json();
    const aiMessage = data.choices[0].message.content;
    
    return {
      success: true,
      message: aiMessage,
      intent,
      actions: extractActions(message, intent),
      suggestions: getSuggestions(intent),
    };
  } catch (error) {
    console.error('AI API error:', error);
    
    // Fall back to mock response
    return {
      success: true,
      message: getMockResponse(message),
      intent,
      actions: extractActions(message, intent),
      suggestions: getSuggestions(intent),
    };
  }
}

// Get contextual suggestions
function getSuggestions(intent) {
  const suggestions = {
    default: [
      'Create a new job',
      'Check production status',
      'View inventory levels',
      'Generate a report',
    ],
    job: [
      'View all jobs',
      'Jobs due this week',
      'Create new job',
      'Update job status',
    ],
    estimate: [
      'New estimate',
      'Recent estimates',
      'Estimate templates',
      'Calculate pricing',
    ],
    permit: [
      'Pending permits',
      'New permit application',
      'Permit status update',
      'Permit requirements',
    ],
    inventory: [
      'Low stock items',
      'Create purchase order',
      'Inventory adjustment',
      'Material costs',
    ],
    production: [
      'Today\'s schedule',
      'Production queue',
      'Job priorities',
      'Capacity report',
    ],
    crm: [
      'Client list',
      'Add new client',
      'Contact history',
      'Recent activities',
    ],
    report: [
      'Sales report',
      'Production report',
      'Inventory report',
      'Profit analysis',
    ],
  };
  
  return suggestions[intent] || suggestions.default;
}

// Workflow automation suggestions
export function getWorkflowSuggestions(jobData) {
  const suggestions = [];
  
  if (jobData.status === 'new') {
    suggestions.push({
      action: 'Create production order',
      priority: 'high',
      description: 'Start production planning for this job',
    });
  }
  
  if (jobData.permitsRequired && !jobData.permitSubmitted) {
    suggestions.push({
      action: 'Submit permit application',
      priority: 'high',
      description: 'Permit required before installation',
    });
  }
  
  if (jobData.designApproved && !jobData.materialsOrdered) {
    suggestions.push({
      action: 'Order materials',
      priority: 'medium',
      description: 'Design approved, ready for material ordering',
    });
  }
  
  return suggestions;
}

// Estimate calculation helper
export function calculateEstimate(params) {
  const {
    signType,
    dimensions,
    materials,
    illumination,
    quantity = 1,
    complexity = 'standard',
  } = params;
  
  // Base prices (simplified for demo)
  const basePrices = {
    'channel-letters': 75, // per inch
    'monument': 5000, // base price
    'pylon': 8000,
    'dimensional-letters': 50, // per inch
    'cabinet-sign': 3000,
  };
  
  const complexityMultipliers = {
    simple: 0.8,
    standard: 1.0,
    complex: 1.3,
    custom: 1.5,
  };
  
  const illuminationAdders = {
    none: 0,
    front: 500,
    halo: 700,
    front_halo: 1000,
  };
  
  let basePrice = basePrices[signType] || 1000;
  
  if (dimensions) {
    if (signType === 'channel-letters' || signType === 'dimensional-letters') {
      basePrice = basePrices[signType] * (dimensions.height || 12) * (dimensions.letterCount || 10);
    } else {
      basePrice = basePrices[signType] * ((dimensions.width || 48) * (dimensions.height || 36) / 1000);
    }
  }
  
  basePrice *= complexityMultipliers[complexity] || 1;
  basePrice += illuminationAdders[illumination] || 0;
  basePrice *= quantity;
  
  return {
    subtotal: basePrice,
    materials: basePrice * 0.4,
    labor: basePrice * 0.35,
    overhead: basePrice * 0.15,
    profit: basePrice * 0.1,
    total: basePrice,
    breakdown: {
      'Materials (40%)': basePrice * 0.4,
      'Labor (35%)': basePrice * 0.35,
      'Overhead (15%)': basePrice * 0.15,
      'Profit (10%)': basePrice * 0.1,
    },
  };
}

export default {
  sendChatMessage,
  getWorkflowSuggestions,
  calculateEstimate,
};
